// The moments worth stopping for, as a pure model.
//
// `Celebration.tsx` is the pixels; this is the question of *what happened*,
// which is a question about the game and therefore testable without a browser.
// Same split as `pieceBadges.ts` and for the same reason.
//
// One moment qualifies, and the bar for a second is high: a celebration takes
// the screen and asks for a click. **A stage turned over** — you beat an army,
// the board grew, and a fresh force took the field. (The artifact delivery
// went with artifact units, §31 R1.)
//
// Not *stored*: read straight off the command log, because the log already
// says a stage turned over and a second copy could only go stale.

import type { Command, GameState } from "src/engine/rules/commands";
import { EMPTY_TALLY, type StageTally } from "src/engine/rules/tally";
import { stagesCleared } from "src/engine/ai/waves";
import type { Stat } from "src/api";

export type Celebration = {
      kind: "stage";
      id: string;
      /** The stage you are now on, which is the one you just earned. */
      stage: number;
      /** Squares of new ground. */
      squares: number;
      /** Pieces in the force that arrived on it. */
      pieces: number;
      /** Promotion squares revealed with the region, if any. */
      promotions: number;
      title: string;
      reward: string;
      detail: string;
      /**
       * How the front you just cleared actually went.
       *
       * The point of the card. It used to be two sentences of prose about the
       * ground you had won, which said the same thing every stage and so said
       * nothing — a player who has just spent forty turns on a camp wants the
       * forty, the pieces it cost them, and what came out of the floor.
       */
      stats: Stat[];
    };

/** One number on the card, with the shortest true word for it: the contract's. */
export type { Stat } from "src/api";

/**
 * What a turn is worth announcing, given the state it produced.
 *
 * Keyed on `ply` rather than on the contents: two crowns delivered on
 * different turns are two celebrations, and the same crown seen across two
 * renders is one. The caller keeps a set of ids it has already shown, which is
 * the whole of the bookkeeping — no flags in state, nothing to reset.
 */
export function celebrationsFor(
  state: GameState,
  log: readonly Command[]
): Celebration[] {
  const out: Celebration[] = [];

  const stage = lastStage(state, log);
  if (stage) out.push(stage);

  return out;
}

/**
 * The stage boundary, read off the end of the log.
 *
 * Only when it is the *last* command, so it announces once: the next move
 * pushes something after it and this stops matching — "what did the thing
 * that just happened do" rather than a record anybody keeps.
 */
function lastStage(state: GameState, log: readonly Command[]): Celebration | null {
  const last = log[log.length - 1];
  if (!last || last.type !== "next-stage") return null;

  const squares = last.cells?.length ?? 0;
  const pieces = last.pieces.length;
  const promotions = last.promotion?.length ?? 0;
  // Stages cleared so far, which is the stage you are now standing in — **off
  // the state, not by counting the log again**. The log answers *whether* a
  // front just turned over, which is a question about this session and rightly
  // stays here; how many have turned over is a fact about the run, and a run
  // is saved as a snapshot with the log thrown away. Counted here, a player who
  // continued a three-front run and then cleared another was congratulated on
  // reaching stage two while the header chip beside it said five.
  const stage = stagesCleared(state) + 1;

  return {
    kind: "stage",
    id: `stage:${stage}`,
    stage,
    squares,
    pieces,
    promotions,
    title: "Front Cleared",
    reward: `Stage ${stage}`,
    // The reducer closed the tally on this very command, so it is the front
    // you just finished rather than the run so far.
    stats: statsOf(state.lastStage ?? EMPTY_TALLY),
    detail: groundText(squares, pieces, promotions)
  };
}

/**
 * The numbers, in the order they happen: you move, you take, that is worth
 * something, you dig.
 *
 * Score sits beside Taken because it is the same event priced rather than
 * counted — a headcount and a value read as one fact when they are adjacent
 * and as two unrelated ones when they are not.
 *
 * Zeroes stay. A row that changes length between stages is a row you have to
 * re-read every time, and "0 ore" is a real answer to a front you cleared
 * without digging — the absent line reads as a card that forgot.
 */
export function statsOf(tally: StageTally): Stat[] {
  return [
    { id: "moves", label: tally.moves === 1 ? "Move" : "Moves", value: tally.moves, tone: "#a8c3ce" },
    { id: "taken", label: "Taken", value: tally.taken, tone: "#d1b566" },
    // `?? 0` for `addTally`'s reason: a run save written before this field
    // decodes without it, and a card drawing nothing is worse than one drawing
    // a zero it can honestly stand behind.
    { id: "score", label: "Score", value: tally.score ?? 0, tone: "#e8a077" },
    { id: "mined", label: "Ore", value: tally.mined, tone: "#8c8268" }
  ];
}

/**
 * What the new ground is, in the fewest words that are still true.
 *
 * One line under the numbers rather than the two sentences it was. The prose
 * said the same thing every stage — the army you beat is always scrap on the
 * floor — and a card that reads identically at stage two and stage nine is a
 * card people stop reading.
 */
function groundText(squares: number, pieces: number, promotions: number): string {
  const parts = [`${squares} new squares`];
  if (pieces > 0) parts.push(`${pieces} defenders`);
  if (promotions > 0) {
    parts.push(`${promotions} promotion ${promotions === 1 ? "square" : "squares"}`);
  }
  return parts.join(" · ");
}

/**
 * One of each, for the dev menu.
 *
 * Here rather than in the component so it is built out of the same tables the
 * real thing is — a preview that drifted from what a real prize looks like
 * would be a debugging tool that debugs a different program.
 */
export function sample(_kind: "stage", id: string): Celebration {
  return {
    kind: "stage",
    id,
    stage: 2,
    squares: 29,
    pieces: 5,
    promotions: 1,
    title: "Front Cleared",
    reward: "Stage 2",
    stats: statsOf({ moves: 41, taken: 7, score: 24, mined: 118 }),
    detail: groundText(29, 5, 1)
  };
}
